import React from "react";
import { IconName, renderLucideIcon } from "./RenderLucideIcon";

export type SectionBadgeProps = React.HTMLAttributes<HTMLSpanElement> & {
    children: React.ReactNode;
    /**
     * `secondary` kam am 12.8.2026 dazu (Jan): Auf dem violetten Hero-Verlauf war
     * der Track-Badge kaum lesbar — `light` faerbt violetten Text (`tertiary-light`)
     * auf violettem Grund, der Kontrast ist minimal.
     *
     * Das Mintgruen (`--color-secondary: #8ce6d1`, `themes/base.css:22`) ist die
     * Farbe, die die Seite fuer Handlungsaufrufe auf dunklem Grund fuehrt
     * (`Button.tsx:50`) — auf Violett hell und ruhig zugleich.
     *
     * ⚠️ NICHT der Wert aus `tailwind.config.js:21` (`#2e4355`, dunkles Blaugrau).
     * Die Datei ist veraltet; es gilt das CSS-Theme. Wer sie liest, waehlt eine
     * Farbe, die auf Violett genauso schlecht liegt wie die alte.
     */
    variant?: "light" | "dark" | "on-dark" | "secondary";
    /**
     * Das Symbol links im Badge. Hat einen Vorgabewert — siehe unten; auf `null`
     * setzen, wenn ein Badge ausnahmsweise ohne auskommen soll.
     */
    icon?: IconName | null;
};

/**
 * Vorgabe-Symbol fuer alle Sektions-Badges (Jan, 6.8.2026).
 *
 * Der Badge sollte sich laut urspruenglichem Entwurf durch seine FORM von
 * Knoepfen unterscheiden — der Kommentar unten hielt fest, sekundaere Knoepfe
 * nutzten `border-2` und `rounded-full`. Inzwischen nutzt der Badge beides
 * selbst, und die Unterscheidung traegt nicht mehr: ein kurzer Text in einer
 * gerundeten, umrandeten Pille liest sich als Knopf, auf den man klicken kann.
 *
 * Das Symbol stellt sie wieder her. Es steht als Vorgabe und nicht an 38
 * Aufrufstellen: die Faehigkeit war seit jeher gebaut (`icon`-Prop,
 * `renderLucideIcon`), nur uebergab sie niemand — ein optionales Feld, das
 * keiner setzt, ist so gut wie nicht vorhanden.
 */
const VORGABE_SYMBOL: IconName = "Sparkles";

export const SectionBadge: React.FC<SectionBadgeProps> = ({
    children,
    variant = "light",
    className = "", // Allow className override
    icon = VORGABE_SYMBOL,
    ...props // Spread other HTML attributes
}) => {
    // New badge design: solid background with rounded corners (not fully rounded like buttons)
    // This makes it clearly distinct from secondary buttons which use border-2 and rounded-full
    // Light variant: purple background with purple text (using original tertiary-light color)
    // Dark variant: teal/primary background with primary text (using original primary color)
    const variantClasses =
        variant === "on-dark"
            ? "bg-white/15 text-white border-white/30"
            : variant === "secondary"
              ? // Mintgruen (#8ce6d1) auf dunklem Grund — siehe `variant` oben.
                "bg-secondary/15 text-secondary border-secondary/40"
              : variant === "light"
                ? "bg-tertiary-light/10 text-tertiary-light border-tertiary-light"
                : "bg-primary/10 text-primary border-primary";

    // If className contains bg-, text-, or border- classes, they will override the variant classes
    // This allows platform-specific colors via className

    return (
        <span
            className={`inline-flex items-center border ${variantClasses} rounded-full px-3 py-1.5 text-xs
                font-semibold mb-6 transition-all duration-200 group ${className}`}
            {...props}
        >
            {/* `w-3.5 h-3.5` statt `w-4 h-4`: der Badge-Text ist `text-xs` (12px),
                ein 16px-Symbol daneben wirkt groesser als die Schrift und zieht den
                Blick auf sich, statt sie nur zu begleiten. `shrink-0`, damit es bei
                langem Text nicht gestaucht wird. */}
            {icon && renderLucideIcon(icon, { className: "w-3.5 h-3.5 mr-1.5 shrink-0" })}
            {children}
        </span>
    );
};

export default SectionBadge;
